import {
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  useSensor,
  MouseSensor,
} from '@dnd-kit/core';
import { useCallback, useState } from 'react';

import {
  getChildrenOfChapter,
  type MicroKnowledgeListType,
  type MicroKnowledgeChapter,
  type MicroKnowledgeItem,
} from 'Pages/Courses/utils/mk-mapped-linked-list';
import { type HandleReorderMk } from 'Pages/Courses/utils/edition-handlers';

const MOUSE_SENSOR_DISTANCE = 5;

export type MicroKnowledgeItemWithoutOrder = Omit<MicroKnowledgeItem, 'previousId' | 'nextId'>;

export const checkItemReachability = (items: Record<string, MicroKnowledgeItem>, activeId: string, itemId: string) => {
  const activeItem = items[activeId];
  const targetItem = items[itemId];

  if (activeItem.type === 'chapter') {
    return activeId !== itemId && targetItem.type === 'chapter' && targetItem.chapterId === null;
  }
  if (targetItem.type === 'chapter') {
    return activeId !== itemId && !targetItem.isCollapsed;
  }
  return (
    activeId !== itemId &&
    targetItem.chapterId !== null &&
    !(items[targetItem.chapterId] as MicroKnowledgeChapter).isCollapsed
  );
};

const getMkListWithoutOrderData = (mkList: MicroKnowledgeItem[]) => {
  return mkList.map((mkItem) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { previousId: _previousId, nextId: _nextId, ...rest } = mkItem;
    return rest;
  });
};

const getLastChapterItem = (items: Record<string, MicroKnowledgeItem>, overId: string) => {
  let tempItem: MicroKnowledgeItem | null = items[overId];
  while (tempItem && tempItem.nextId !== null && items[tempItem.nextId].type !== 'chapter') {
    tempItem = tempItem.nextId ? items[tempItem.nextId] : null;
  }
  return tempItem;
};

const completeReachableToFull = (
  mkList: MicroKnowledgeListType,
  reachable: MicroKnowledgeItemWithoutOrder[],
  draggedId: string,
) => {
  const { items } = mkList;
  return reachable.flatMap((partialMk, idx) => {
    const mk = items[partialMk.id];
    const nextMk = reachable[idx + 1] ?? null;

    if (mk.type === 'chapter' && (!nextMk || nextMk.chapterId !== partialMk.id)) {
      const childrenIds = getChildrenOfChapter(mkList, mk);
      const children = childrenIds.reduce<MicroKnowledgeItemWithoutOrder[]>((children, id) => {
        if (id === draggedId) {
          return children;
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { previousId: _previousId, nextId: _nextId, ...item } = items[id];

        return [...children, item];
      }, []);
      return [partialMk, ...children];
    }

    return partialMk;
  });
};

export type MkDragAndDropProps = {
  mkList: MicroKnowledgeListType;
  microKnowledgeArray: MicroKnowledgeItem[];
  onReorder: (params: HandleReorderMk) => void;
  onToggleCollapse: ({ id }: { id: string }) => void;
};

export const useMkDragAndDrop = ({ mkList, microKnowledgeArray, onReorder, onToggleCollapse }: MkDragAndDropProps) => {
  const { items } = mkList;

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: MOUSE_SENSOR_DISTANCE },
    }),
  );

  const [activeItem, setActiveItem] = useState<MicroKnowledgeItem | null>(null);
  const [overItem, setOverItem] = useState<MicroKnowledgeItem | null>(null);
  const [dragDirection, setDragDirection] = useState<-1 | 0 | 1>(0);
  const [initialChapterCollapseState, setInitialChapterCollapseState] = useState<Record<string, boolean>>({});

  /**
   * Start of the drag
   * Save the collapse state of the chapters
   * If the active item is a chapter, we need to collapse all chapters
   * If the active item is not a chapter, we need don't change the collapse state
   */
  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const chapterCollapseState: Record<string, boolean> = {};
      setActiveItem(items[active.id]);

      const activeItem = items[active.id];
      if (activeItem.type === 'chapter') {
        Object.values(items).forEach((item) => {
          if (item.type === 'chapter') {
            chapterCollapseState[item.id] = item.isCollapsed;
            if (!item.isCollapsed) {
              onToggleCollapse({ id: item.id });
            }
          }
        });
      }
      setInitialChapterCollapseState(chapterCollapseState);
    },
    [items, setActiveItem, onToggleCollapse],
  );

  /**
   * Drag over
   * If the active item is a chapter
   *   And the overed item is a chapter - make sure the overed item is collapsed
   * If the active item is a MK
   *   And the over item is a collapsed chapter - We uncollapse the chapter
   *   And the over item is an uncollapsed chapter - NOOP
   *   And the over item is a MK - NOOP
   */
  const handleDragOver = useCallback(
    ({ active, over, delta }: DragOverEvent) => {
      if (over) {
        setOverItem(items[over.id]);

        const activeItem = items[active.id];
        const overItem = items[over.id];

        if (activeItem.type === 'chapter' && overItem.type === 'chapter' && !overItem.isCollapsed) {
          onToggleCollapse({ id: overItem.id });
        }
        if (activeItem.type !== 'chapter' && overItem.type === 'chapter' && overItem.isCollapsed) {
          onToggleCollapse({ id: overItem.id });
        }
      }
      setDragDirection(Math.sign(delta.y) as -1 | 0 | 1);
    },
    [items, setOverItem, onToggleCollapse],
  );

  const handleDragCancel = useCallback(() => {
    setActiveItem(null);
    setOverItem(null);
    setDragDirection(0);
  }, [setActiveItem, setOverItem, setDragDirection]);

  /**
   * Drag end
   * Restore the collapse state of the chapters
   * Reorder the MKs
   */
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over, delta } = event;
      const activeId = active.id.toString();
      const activeItem = items[activeId];
      const overId = over?.id?.toString() ?? null;
      const overItem = over ? items[over.id] : null;

      // move a chapter up - puts the active chapter under the overed chapter
      if (activeItem && activeItem.type === 'chapter' && overItem?.type === 'chapter' && delta.y < 0) {
        const itemsWithoutOrderData = getMkListWithoutOrderData(Object.values(items));
        const mkListWithoutActiveChapter = itemsWithoutOrderData.filter(
          (mkItem) => mkItem.id !== activeId && mkItem.chapterId !== activeId,
        );
        const activeChapterAndChildMk = itemsWithoutOrderData.filter(
          (mkItem) => mkItem.id === activeId || mkItem.chapterId === activeId,
        );

        const overItemIdx = mkListWithoutActiveChapter.findIndex((mkItem) => mkItem.id === overId);
        const reorderedMklist = [...mkListWithoutActiveChapter];
        reorderedMklist.splice(overItemIdx + (delta.y > 0 ? 1 : 0), 0, ...activeChapterAndChildMk);
        const nextOrderedMks = completeReachableToFull(mkList, reorderedMklist, activeId);
        Object.values(mkList.items).forEach((mk) => {
          if (mk.type === 'chapter' && mk.isCollapsed !== initialChapterCollapseState[mk.id]) {
            onToggleCollapse({ id: mk.id });
          }
        });
        onReorder({ nextOrderedMks });
      }

      // move a chapter down - puts the active chapter over the overed chapter
      if (activeItem && activeItem.type === 'chapter' && overId && overItem?.type === 'chapter' && delta.y > 0) {
        const itemsWithoutOrderData = getMkListWithoutOrderData(Object.values(items));
        const mkListWithoutActiveChapter = itemsWithoutOrderData.filter(
          (mkItem) => mkItem.id !== activeId && mkItem.chapterId !== activeId,
        );
        const activeChapterAndChildMk = itemsWithoutOrderData.filter(
          (mkItem) => mkItem.id === activeId || mkItem.chapterId === activeId,
        );

        const lastChapterItem = getLastChapterItem(items, overId);
        if (!lastChapterItem) {
          throw new Error('Last chapter item not found');
        }

        const lastChapterItemIdx = mkListWithoutActiveChapter.findIndex((item) => item.id === lastChapterItem.id);
        const reorderedMklist = [...mkListWithoutActiveChapter];
        reorderedMklist.splice(lastChapterItemIdx + 1, 0, ...activeChapterAndChildMk);
        const nextOrderedMks = completeReachableToFull(mkList, reorderedMklist, activeId);
        Object.values(mkList.items).forEach((mk) => {
          if (mk.type === 'chapter' && mk.isCollapsed !== initialChapterCollapseState[mk.id]) {
            onToggleCollapse({ id: mk.id });
          }
        });
        onReorder({ nextOrderedMks });
      }

      // move a mk on a chapter item
      if (
        activeItem &&
        activeItem.type !== 'chapter' &&
        overId &&
        overItem &&
        overItem?.type === 'chapter' &&
        overItem.id !== activeItem.chapterId
      ) {
        const lastChapterItem = getLastChapterItem(items, overId);

        const mkListWithoutOrderData = getMkListWithoutOrderData(Object.values(items));

        const reorderedMklist = mkListWithoutOrderData.filter((item) => item.id !== activeId);
        const activeItem = mkListWithoutOrderData.find((item) => item.id === activeId);
        if (!activeItem) {
          throw new Error('Active item not found');
        }
        activeItem.chapterId = overId;

        const lastChapterItemIdx = mkListWithoutOrderData.findIndex((item) => item.id === lastChapterItem?.id);
        reorderedMklist.splice(lastChapterItemIdx + 1, 0, activeItem);

        const nextOrderedMks = completeReachableToFull(mkList, reorderedMklist, activeId);
        Object.values(mkList.items).forEach((mk) => {
          if (mk.type === 'chapter' && mk.isCollapsed !== initialChapterCollapseState[mk.id]) {
            onToggleCollapse({ id: mk.id });
          }
        });
        onReorder({ nextOrderedMks });
      }

      // move a mk on a mk item up/down
      if (activeItem && activeItem.type !== 'chapter' && overItem && overItem?.type !== 'chapter') {
        const mkListWithoutOrderData = getMkListWithoutOrderData(Object.values(items));
        const activeItem = mkListWithoutOrderData.find((item) => item.id === activeId);
        if (!activeItem) {
          throw new Error('Active item not found');
        }
        activeItem.chapterId = overItem.chapterId;

        const overItemIdx = mkListWithoutOrderData.findIndex((item) => item.id === overId);
        const reorderedMklist = mkListWithoutOrderData.filter((item) => item.id !== activeId);
        reorderedMklist.splice(overItemIdx + (delta.y > 0 ? 1 : 0), 0, activeItem);

        reorderedMklist.forEach((mk) => {
          if (mk.type === 'chapter' && mk.isCollapsed !== initialChapterCollapseState[mk.id]) {
            onToggleCollapse({ id: mk.id });
          }
        });

        const nextOrderedMks = completeReachableToFull(mkList, reorderedMklist, activeId);
        onReorder({ nextOrderedMks });
      }

      // clean
      setActiveItem(null);
      setOverItem(null);
      setDragDirection(0);
      setInitialChapterCollapseState({});
    },
    [mkList, items, microKnowledgeArray, setActiveItem, setOverItem, onToggleCollapse],
  );

  return {
    activeItem,
    overItem,
    dragDirection,
    attributes: { sensors },
    listeners: {
      onDragStart: handleDragStart,
      onDragOver: handleDragOver,
      onDragCancel: handleDragCancel,
      onDragEnd: handleDragEnd,
    },
  };
};
